import { isEmpty, uniq } from 'lodash';
import { call, put, SagaReturnType, select, takeEvery } from 'redux-saga/effects';
import { doSucceededGetPositionList, doSucceededGetQuoteList } from 'src/actions';
import { State, Type } from 'src/actions/utils';
import { getClientApi, PositionApi, PrivateQuoteApi, QuoteApi } from 'src/data-communication';
import { GetPositionListDto } from 'src/dtos';
import { FromTradingBlockServerEvent } from 'src/events';
import { mapPositionListDtoToModel } from 'src/mappers';
import { Position } from 'src/models';
import { Maybe, Logger } from 'src/utils';

import { TReduxAction } from '../typings/commonTypes';

import { safeSaga } from './utils';

function* list(action: TReduxAction) {
  const { authToken } = yield select(state => state.auth.data);
  const dto: GetPositionListDto = action?.payload;
  const { data, total }: SagaReturnType<PositionApi['list']> = yield call(getClientApi().position.list, {
    ...dto,
    authToken,
  });
  const privateQuotesResponse: SagaReturnType<PrivateQuoteApi['list']> = yield call(
    getClientApi().privateQuote.list,
    authToken,
  );

  const positionList = mapPositionListDtoToModel(data, privateQuotesResponse.data);
  yield put(doSucceededGetPositionList({ data: positionList, total }));
}

function* updateListOnOrderUpdateEvent(action: TReduxAction) {
  try {
    const orderUpdateEvent: FromTradingBlockServerEvent.OrderUpdateEventPayload = action.payload;

    if (orderUpdateEvent.fillQuantity === 0) {
      return;
    }

    let positionList: Maybe<Position[]> = yield select(state => state.positions.list.data?.data);

    if (isEmpty(positionList)) {
      return;
    }

    const { authToken } = yield select(state => state.auth.data);
    const { data, total }: SagaReturnType<PositionApi['list']> = yield call(getClientApi().position.list, {
      params: {
        accountId: orderUpdateEvent.accountId,
      },
      authToken,
    });
    const privateQuotesResponse: SagaReturnType<PrivateQuoteApi['list']> = yield call(
      getClientApi().privateQuote.list,
      authToken,
    );

    positionList = mapPositionListDtoToModel(data, privateQuotesResponse.data);
    yield put(doSucceededGetPositionList({ data: positionList, total }));

    const symbols = uniq(positionList.map(position => position.symbol)).join(',');
    const quoteListResponse: SagaReturnType<QuoteApi['list']> = yield call(getClientApi().quote.list, {
      queryParams: {
        symbols,
      },

      authToken,
    });
    yield put(doSucceededGetQuoteList(quoteListResponse));
  } catch (error: any) {
    Logger.error('Error updating position list on order update event.');
    Logger.error(error);
  }
}

export function* registerPositionSagas() {
  yield takeEvery(State.actionRequested(Type.GET_POSITION_LIST), safeSaga(list, Type.GET_POSITION_LIST));
  yield takeEvery(Type.UPDATE_ORDER_FROM_EVENT, updateListOnOrderUpdateEvent);
}
